import { useRef, useState } from "react";
import { Loader2, ScanBarcode } from "lucide-react";
import LiveBarcodeScanner from "./LiveBarcodeScanner";
import { decodeBarcodeFromFile } from "../lib/barcode/scanner";

interface Props {
  onBarcode: (input: { barcode: string; imageDataUrl?: string }) => void;
  busy: boolean;
}

function canUseLive(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof window !== "undefined" &&
    window.isSecureContext
  );
}

export default function BarcodeCapture({ onBarcode, busy }: Props) {
  const [forcePhoto, setForcePhoto] = useState(false);
  const useLive = canUseLive() && !forcePhoto;

  // While the parent is looking up the decoded barcode, render a static panel
  // instead of the scanner. This unmounts the camera + video element, freeing
  // memory (iOS Safari can reload a PWA under memory pressure, which would
  // dump the user back to the initial state).
  if (busy) {
    return (
      <div className="p-4 space-y-4">
        <div className="p-6 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
          <span className="text-slate-300">Looking up product…</span>
        </div>
      </div>
    );
  }

  if (useLive) {
    return (
      <>
        <LiveBarcodeScanner
          onDecoded={(code) => onBarcode({ barcode: code })}
          // Auto-switch to photo fallback when the camera can't start
          // (permission denied, no device, etc). Without this the user is
          // stuck on a tab they can't use.
          onCameraUnavailable={(kind) => {
            if (kind === "denied" || kind === "no-device" || kind === "insecure") {
              // Tiny delay so the user sees the explanation before we swap views.
              setTimeout(() => setForcePhoto(true), 1500);
            }
          }}
        />
        <div className="px-4 -mt-2">
          <button
            onClick={() => setForcePhoto(true)}
            className="text-sm text-slate-400 underline"
          >
            Trouble scanning? Use photo instead
          </button>
        </div>
      </>
    );
  }

  return (
    <SnapPhotoBarcode
      onBarcode={onBarcode}
      busy={busy}
      onSwitchToLive={canUseLive() ? () => setForcePhoto(false) : undefined}
    />
  );
}

interface SnapProps {
  onBarcode: (input: { barcode: string; imageDataUrl?: string }) => void;
  busy: boolean;
  onSwitchToLive?: () => void;
}

function SnapPhotoBarcode({ onBarcode, busy, onSwitchToLive }: SnapProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickPhoto() {
    setError(null);
    fileInput.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(await fileToDataUrl(f));
  }

  async function submit() {
    if (!file || !preview) return;
    setError(null);
    setDecoding(true);
    try {
      const code = await decodeBarcodeFromFile(file);
      onBarcode({ barcode: code, imageDataUrl: preview });
    } catch (err) {
      const msg = String((err as Error)?.message ?? err);
      setError(
        msg.includes("NotFoundException") || msg.includes("No MultiFormat Readers")
          ? "Couldn't read the barcode. Try again with the barcode centered, in focus, and well-lit."
          : msg
      );
    } finally {
      setDecoding(false);
    }
  }

  function reset() {
    setPreview(null);
    setFile(null);
    setError(null);
    if (fileInput.current) fileInput.current.value = "";
  }

  return (
    <div className="p-4 space-y-4">
      <p className="text-sm text-slate-400">
        Snap a clear, centered photo of the barcode. (Live scanning needs HTTPS — switch the URL or use this.)
      </p>

      {preview ? (
        <img src={preview} alt="Barcode preview" className="w-full rounded-lg border border-slate-800" />
      ) : (
        <button
          onClick={pickPhoto}
          className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-700 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors gap-2"
        >
          <ScanBarcode className="w-12 h-12" strokeWidth={1.5} />
          <span>Photograph the barcode</span>
        </button>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
      />

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        {preview && (
          <button
            onClick={reset}
            disabled={busy || decoding}
            className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-50"
          >
            Retake
          </button>
        )}
        <button
          onClick={submit}
          disabled={!preview || busy || decoding}
          className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-40"
        >
          {decoding ? "Decoding..." : busy ? "Looking up..." : "Look up"}
        </button>
      </div>

      {onSwitchToLive && (
        <button onClick={onSwitchToLive} className="w-full text-sm text-slate-400 underline">
          Back to live scanner
        </button>
      )}
    </div>
  );
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
